import { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';

import { Skeleton } from '@fhs/ui';
import { selectors, useAppSelector } from '@fhs-legacy/frontend/src/state/global-state';
import { ICartEntry } from '@rbi-ctg/menu';

import { calculateItemPrice } from './calculate-item-price';
import { FreePriceDisplay, OfferPriceDisplay, OriginalPriceDisplay } from './item-price-components';

interface ItemPriceProps {
  item: ICartEntry;
}

export default function ItemPrice({ item }: ItemPriceProps) {
  const { selectedOffer, selectedOfferSelections, offerEligibleItem } = useAppSelector(state => ({
    selectedOffer: selectors.loyalty.selectSelectedOffer(state),
    selectedOfferSelections: selectors.loyalty.selectSelectedOfferSelections(state),
    offerEligibleItem: selectors.loyalty.selectOfferEligibleItem(state, item.cartId),
  }));

  const offersLoading = useAppSelector(selectors.loyalty.selectOffersLoading);

  const hasRewardSelectionForItem = useMemo(
    () => selectedOfferSelections.some(selection => selection.lineId === item.cartId),
    [selectedOfferSelections, item.cartId]
  );

  const priceData = useMemo(
    () => calculateItemPrice(item, selectedOffer, offerEligibleItem),
    [item, selectedOffer, offerEligibleItem]
  );

  if (offersLoading && offerEligibleItem && !hasRewardSelectionForItem) {
    return <Skeleton style={styles.pricesSkeleton} />;
  }

  const isOfferEligibleItem = !!offerEligibleItem;

  const finalPrice = isOfferEligibleItem
    ? priceData.originalPrice
    : priceData.finalItemPrice === 0
      ? priceData.originalPrice
      : priceData.finalItemPrice;

  const formattedPrice = (finalPrice / 100).toFixed(2);
  const priceLabel =
    priceData.isFree && !isOfferEligibleItem
      ? 'Item is free'
      : priceData.shouldShowStrikeThrough
        ? `Original price ${formattedPrice}, discounted`
        : `Price ${formattedPrice}`;

  return (
    <View
      testID={`item-price-${item.cartId}`}
      style={styles.prices}
      accessibilityRole="text"
      accessibilityLabel={priceLabel}
    >
      <OriginalPriceDisplay
        price={finalPrice}
        shouldShowStrikeThrough={priceData.shouldShowStrikeThrough}
      />

      {/* Reward path: "Free" only (no tag). Offer path: tag + "Free" or tag + price on same row only. */}
      <FreePriceDisplay showFree={priceData.isFree && !isOfferEligibleItem} />

      {isOfferEligibleItem && <OfferPriceDisplay price={priceData.finalItemPrice} />}
    </View>
  );
}

const styles = StyleSheet.create({
  prices: {
    alignItems: 'flex-end',
  },
  pricesSkeleton: {
    height: 18,
  },
});
